import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AddIcon from "@material-ui/icons/Add";
import Search from "@material-ui/icons/Search";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Close from "@material-ui/icons/Close";
import Edit from "@material-ui/icons/Edit";
import LocalShipping from "@material-ui/icons/LocalShipping";
import CheckCircle from "@material-ui/icons/CheckCircle";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import Snackbar from "components/Snackbar/Snackbar.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);
const useDrawerStyles = makeStyles({
  paper: { width: 440, display: "flex", flexDirection: "column" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px 12px",
  },
  body: { flex: 1, overflowY: "auto", padding: "0 20px 20px" },
  footer: { padding: "12px 20px", borderTop: "1px solid #e0e0e0", display: "flex", gap: "10px", justifyContent: "flex-end" },
  field: { marginBottom: "14px !important", width: "100%" },
  sectionLabel: { fontSize: "12px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", margin: "16px 0 8px" },
});

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SUPPLIERS_QUERY = `
  query GetSuppliers($page: Int!, $pageSize: Int!, $search: String) {
    suppliersWithPagination(filter: { page: $page, pageSize: $pageSize, search: $search }) {
      data {
        id supplierNumber name phone email
        currentCreditAmount totalPurchase totalPurchaseExcludeReturns
        isActive company supplierGroup note taxRegNumber bank bankAccountNumber
        address location ward
      }
      total page pageSize totalPages
    }
  }
`;

const SAVE_MUTATION = `
  mutation SaveSupplier($data: CreateSupplierDto!) {
    createOrUpdateSupplier(data: $data)
  }
`;

const TOGGLE_MUTATION = `
  mutation ToggleSupplier($id: String!) {
    toggleSupplierStatus(id: $id)
  }
`;

function formatNumber(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

function authFetch(body) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
    credentials: "include",
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

const EMPTY_FORM = {
  supplierNumber: "", name: "", phone: "", email: "",
  address: "", location: "", ward: "", company: "",
  taxRegNumber: "", supplierGroup: "", note: "",
  bank: "", bankAccountNumber: "", isActive: true,
};

function supplierToForm(s) {
  return {
    supplierNumber: s.supplierNumber || "",
    name: s.name || "",
    phone: s.phone || "",
    email: s.email || "",
    address: s.address || "",
    location: s.location || "",
    ward: s.ward || "",
    company: s.company || "",
    taxRegNumber: s.taxRegNumber || "",
    supplierGroup: s.supplierGroup || "",
    note: s.note || "",
    bank: s.bank || "",
    bankAccountNumber: s.bankAccountNumber || "",
    isActive: s.isActive !== false,
  };
}

export default function SuppliersPage() {
  const classes = useStyles();
  const drawerClasses = useDrawerStyles();

  const [suppliers, setSuppliers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef("");
  const debounceRef = React.useRef(null);
  const [notify, setNotify] = React.useState({ open: false, message: "" });

  // Edit drawer
  const [drawer, setDrawer] = React.useState({ open: false, supplierId: null });
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);

  const showNotify = (message) => {
    setNotify({ open: true, message });
    setTimeout(() => setNotify((n) => ({ ...n, open: false })), 4000);
  };

  const fetchSuppliers = React.useCallback((pg, ps, srch) => {
    setLoading(true);
    setError("");
    authFetch({ query: SUPPLIERS_QUERY, variables: { page: pg, pageSize: ps, search: srch || undefined } })
      .then(({ data, errors }) => {
        if (errors) { setError(errors[0].message); return; }
        const p = data.suppliersWithPagination;
        setSuppliers(p.data);
        setTotal(p.total);
        setTotalPages(p.totalPages);
        setPage(p.page);
        setPageInput(String(p.page));
      })
      .catch(() => setError("Cannot connect to server"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { fetchSuppliers(1, pageSize, ""); }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    searchRef.current = val;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuppliers(1, pageSize, val), 400);
  };

  const handlePageSizeChange = (e) => {
    const ps = e.target.value;
    setPageSize(ps);
    fetchSuppliers(1, ps, searchRef.current);
  };

  const goToPage = (pg) => {
    if (pg < 1 || pg > totalPages) return;
    fetchSuppliers(pg, pageSize, searchRef.current);
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setDrawer({ open: true, supplierId: null });
  };

  const openEdit = (s) => {
    setForm(supplierToForm(s));
    setDrawer({ open: true, supplierId: s.id });
  };

  const closeDrawer = () => setDrawer({ open: false, supplierId: null });

  const handleSave = () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const data = { ...form };
    if (drawer.supplierId) data.id = drawer.supplierId;
    authFetch({ query: SAVE_MUTATION, variables: { data } })
      .then(({ errors }) => {
        if (errors) { alert(errors[0].message); return; }
        showNotify(drawer.supplierId ? "Supplier updated!" : "Supplier added!");
        closeDrawer();
        fetchSuppliers(page, pageSize, searchRef.current);
      })
      .catch(() => alert("Cannot connect to server"))
      .finally(() => setSaving(false));
  };

  const handleToggle = (s) => {
    authFetch({ query: TOGGLE_MUTATION, variables: { id: s.id } })
      .then(({ errors }) => {
        if (errors) { alert(errors[0].message); return; }
        fetchSuppliers(page, pageSize, searchRef.current);
      });
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = suppliers.map((s) => [
    <IconButton key={s.id} size="small" onClick={() => openEdit(s)}>
      <Edit fontSize="small" />
    </IconButton>,
    s.supplierNumber || "—",
    <span key={s.id + "-name"} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      {s.name}
      {!s.isActive && (
        <span style={{ fontSize: "11px", fontWeight: 600, padding: "1px 7px", borderRadius: "10px", backgroundColor: "#f5f5f5", color: "#9e9e9e", border: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
          Inactive
        </span>
      )}
    </span>,
    s.phone || "—",
    formatNumber(s.currentCreditAmount),
    formatNumber(s.totalPurchase),
    formatNumber(s.totalPurchaseExcludeReturns),
  ]);

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="warning" icon>
              <CardIcon color="warning">
                <LocalShipping />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Suppliers</h4>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={openNew}
                style={{ position: "absolute", right: "16px", top: "16px" }}
              >
                Add supplier
              </Button>
            </CardHeader>
            <CardBody>
              {error && <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>}

              {/* Search */}
              <div style={{ display: "flex", alignItems: "center", border: "1px solid #ccc", borderRadius: "4px", padding: "4px 10px", marginBottom: "12px", maxWidth: "360px" }}>
                <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "20px" }} />
                <InputBase
                  placeholder="Search by name, code or phone..."
                  value={search}
                  onChange={handleSearchChange}
                  style={{ flex: 1, fontSize: "14px" }}
                />
              </div>

              <Table
                tableHead={["", "Code", "Name", "Phone", "Credit amount", "Total purchase", "Total (ex. returns)"]}
                tableData={loading ? [] : tableData}
                customCellClasses={[classes.center, classes.right, classes.right, classes.right]}
                customClassesForCells={[0, 4, 5, 6]}
                customHeadCellClasses={[classes.center, classes.right, classes.right, classes.right]}
                customHeadClassesForCells={[0, 4, 5, 6]}
              />
              {loading && <p style={{ textAlign: "center", padding: "16px 0" }}>Loading...</p>}

              {/* Pagination */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0", flexWrap: "wrap", fontSize: "14px" }}>
                <span>Show</span>
                <Select value={pageSize} onChange={handlePageSizeChange} disableUnderline
                  style={{ fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px", padding: "2px 8px" }}>
                  {PAGE_SIZE_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt} rows</MenuItem>)}
                </Select>
                <IconButton size="small" onClick={() => goToPage(1)} disabled={page <= 1}><FirstPage fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => goToPage(page - 1)} disabled={page <= 1}><ChevronLeft fontSize="small" /></IconButton>
                <input value={pageInput} onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { const pg = parseInt(pageInput, 10); if (!isNaN(pg)) goToPage(pg); } }}
                  onBlur={() => { const pg = parseInt(pageInput, 10); if (!isNaN(pg)) goToPage(pg); else setPageInput(String(page)); }}
                  style={{ width: "48px", textAlign: "center", border: "1px solid #ccc", borderRadius: "4px", padding: "4px 0", fontSize: "14px" }} />
                <IconButton size="small" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}><ChevronRight fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => goToPage(totalPages)} disabled={page >= totalPages}><LastPage fontSize="small" /></IconButton>
                <span style={{ color: "#555" }}>{from} – {to} of {total.toLocaleString("vi-VN")} suppliers</span>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ── Edit / Create Drawer ── */}
      <Drawer anchor="right" open={drawer.open} onClose={closeDrawer} classes={{ paper: drawerClasses.paper }}>
        {/* Header */}
        <div className={drawerClasses.header}>
          <span style={{ fontWeight: 700, fontSize: "16px" }}>
            {drawer.supplierId ? "Edit supplier" : "Add supplier"}
          </span>
          <IconButton size="small" onClick={closeDrawer}><Close fontSize="small" /></IconButton>
        </div>
        <Divider />

        {/* Form body */}
        <div className={drawerClasses.body}>
          <div className={drawerClasses.sectionLabel}>Basic info</div>

          <TextField className={drawerClasses.field} label="Supplier number" value={form.supplierNumber} onChange={setField("supplierNumber")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Supplier name *" value={form.name} onChange={setField("name")} variant="outlined" size="small" required />
          <TextField className={drawerClasses.field} label="Phone" value={form.phone} onChange={setField("phone")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Email" value={form.email} onChange={setField("email")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Company" value={form.company} onChange={setField("company")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Supplier group" value={form.supplierGroup} onChange={setField("supplierGroup")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Tax registration number" value={form.taxRegNumber} onChange={setField("taxRegNumber")} variant="outlined" size="small" />

          <div className={drawerClasses.sectionLabel}>Address</div>
          <TextField className={drawerClasses.field} label="Address" value={form.address} onChange={setField("address")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Location / District" value={form.location} onChange={setField("location")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Ward" value={form.ward} onChange={setField("ward")} variant="outlined" size="small" />

          <div className={drawerClasses.sectionLabel}>Payment</div>
          <TextField className={drawerClasses.field} label="Bank" value={form.bank} onChange={setField("bank")} variant="outlined" size="small" />
          <TextField className={drawerClasses.field} label="Bank account number" value={form.bankAccountNumber} onChange={setField("bankAccountNumber")} variant="outlined" size="small" />

          <div className={drawerClasses.sectionLabel}>Other</div>
          <TextField className={drawerClasses.field} label="Note" value={form.note} onChange={setField("note")} variant="outlined" size="small" multiline rows={2} />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} color="primary" />}
            label={form.isActive ? "Active" : "Inactive"}
          />
        </div>

        <Divider />
        {/* Footer */}
        <div className={drawerClasses.footer}>
          <Button variant="outlined" onClick={closeDrawer}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </Drawer>

      <Snackbar
        place="tr"
        color="success"
        icon={CheckCircle}
        message={notify.message}
        open={notify.open}
        closeNotification={() => setNotify((n) => ({ ...n, open: false }))}
        close
      />
    </>
  );
}
